// ###### IMPORTS
import React, { useState, useEffect, useCallback } from 'react';
import { XMarkIcon } from '@heroicons/react/24/solid';
import { toast } from 'react-hot-toast';
import { saveBalanceSnapshotWithItems } from '../../viewServices/assets_api';
import {
  emptyRow,
  buildItemsPayloadFromRows,
  formatApiError,
} from '../../viewServices/snapshot_form_shared';
import SpinnerLoad from 'views/finance/moduleComponents/SpinnerLoad';
import DatePicker from 'shared_items/DatePicker';
import SnapshotEditableItemsTable from './SnapshotEditableItemsTable';

// ###### COMPONENT — self-contained create snapshot (same role as ModalAssetAdd)
const ModalSnapshotAdd = ({ isOpen, onClose, onSuccess, assets = [] }) => {
  const [header, setHeader] = useState({
    snapshot_date: '',
    is_closing: true,
    notes: '',
  });
  const [rows, setRows] = useState([emptyRow()]);
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    if (!isOpen) return;
    setHeader({ snapshot_date: '', is_closing: true, notes: '' });
    setRows([emptyRow()]);
  }, [isOpen]);

  const resetAndClose = useCallback(() => {
    onClose?.();
  }, [onClose]);

  const handleHeaderChange = (e) => {
    const { name, value, type, checked } = e.target;
    setHeader((prev) => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value,
    }));
  };

  const updateRow = (key, field, value) => {
    setRows((prev) => prev.map((r) => (r.key === key ? { ...r, [field]: value } : r)));
  };
  const addRow = () => setRows((prev) => [...prev, emptyRow()]);
  const removeRow = (key) => {
    setRows((prev) => (prev.length <= 1 ? prev : prev.filter((r) => r.key !== key)));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!header.snapshot_date?.trim()) {
      toast.error('Select a snapshot date.');
      return;
    }
    const itemsPayload = buildItemsPayloadFromRows(rows, assets);
    if (!itemsPayload) return;

    try {
      setSubmitting(true);
      await saveBalanceSnapshotWithItems({
        snapshot_date: header.snapshot_date,
        is_closing: !!header.is_closing,
        notes: header.notes?.trim() || '',
        items: itemsPayload,
      });
      toast.success('Snapshot created.');
      onSuccess?.();
      resetAndClose();
    } catch (err) {
      toast.error(formatApiError(err.response?.data) || err.message || 'Failed to save.');
    } finally {
      setSubmitting(false);
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex justify-center items-center z-50 p-3">
      <div className="bg-white rounded-2xl shadow-xl w-full max-w-4xl relative max-h-[92vh] flex flex-col">
        {submitting && (
          <div className="absolute inset-0 flex items-center justify-center z-10 bg-white/90 rounded-2xl">
            <div className="bg-white rounded-xl shadow-lg p-4 border border-gray-200">
              <SpinnerLoad />
            </div>
          </div>
        )}

        <div className="flex items-start justify-between gap-2 px-4 pt-4 pb-2 border-b border-gray-100 shrink-0">
          <h2 className="text-lg font-semibold text-gray-900 pr-2">New snapshot</h2>
          <button
            type="button"
            onClick={resetAndClose}
            disabled={submitting}
            className="text-gray-500 hover:text-gray-700 p-1"
            title="Close"
          >
            <XMarkIcon className="h-6 w-6" />
          </button>
        </div>

        <form onSubmit={handleSubmit} className="flex flex-col flex-1 min-h-0">
          <div className="overflow-y-auto px-4 py-3 space-y-4 flex-1">
            <div>
              <label className="block text-xs text-gray-500 mb-0.5">Snapshot date</label>
              <div className="flex flex-wrap items-center gap-4">
                <DatePicker
                  name="snapshot_date"
                  value={header.snapshot_date}
                  onChange={handleHeaderChange}
                  mode="single"
                  colorScheme="green"
                  disabled={submitting}
                  placeholder="Select date"
                  className="w-48 justify-between min-h-[2.25rem] shrink-0"
                />
                <label className="inline-flex items-center gap-2 text-sm text-gray-700 cursor-pointer shrink-0">
                  <input
                    type="checkbox"
                    name="is_closing"
                    checked={header.is_closing}
                    onChange={handleHeaderChange}
                    disabled={submitting}
                    className="rounded border-gray-300 text-green-700 focus:ring-green-600"
                  />
                  Closing snapshot
                </label>
              </div>
            </div>

            <div>
              <label className="block text-xs text-gray-500 mb-0.5">Notes</label>
              <textarea
                name="notes"
                value={header.notes}
                onChange={handleHeaderChange}
                rows={2}
                disabled={submitting}
                className="w-full px-2 py-1.5 border rounded-md text-sm resize-y min-h-[2.5rem]"
                placeholder="Optional"
              />
            </div>

            <SnapshotEditableItemsTable
              rows={rows}
              updateRow={updateRow}
              addRow={addRow}
              removeRow={removeRow}
              assets={assets}
              submitting={submitting}
            />
          </div>

          <div className="flex shrink-0 flex-wrap items-center gap-x-3 gap-y-1 border-t border-gray-100 bg-gray-50 px-4 py-3 rounded-b-2xl">
            <p className="min-w-0 flex-1 text-left text-xs text-gray-500">
              At least one line with an asset and value is required.
            </p>
            <button
              type="button"
              onClick={resetAndClose}
              disabled={submitting}
              className="shrink-0 rounded-xl border border-gray-300 px-4 py-2 text-sm text-gray-700 hover:bg-gray-100"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={submitting}
              className={`shrink-0 rounded-xl px-5 py-2 text-sm font-medium text-white ${
                submitting ? 'cursor-not-allowed bg-green-400' : 'bg-green-700 hover:bg-green-800'
              }`}
            >
              Save
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default ModalSnapshotAdd;
